import { useContext, useEffect, useState } from 'react';
import { Button, Grid, MenuItem, Select, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { datePickerFormat } from 'helpers';
import { EstateResultItem, EstateResultItem_OneViewCollectionInput } from 'queries';

import { EstateResultsPageContext } from 'pages/estate-results-page/context/estate-results-page-context';

import {
  CustomResultItemField,
  handleNewCustomFieldButtonClick,
  parseCustomFieldsJson,
  removeCustomField,
  updateCustomField,
} from '../utils/custom-result-item-field';

import NewEssentialData from './new-essential-data';

type Props = {
  estateResultItem?: EstateResultItem;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setEssentialData: Function;
};

const useStyles = makeStyles(() => ({
  button: {
    marginTop: '3px',
    padding: '2px 10px',
  },
  container: {
    paddingTop: '10px',
  },
  inputField: {
    textAlign: 'left',
    width: '100%',
  },
  dateField: {
    paddingTop: '10px',
    textAlign: 'left',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
  },
  datePicker: {
    padding: '5px',
  },
  selectContainer: {
    textAlign: 'left',
  },
  title: {
    paddingTop: '10px',
  },
}));

export default function AddCollectionItem({ setEssentialData, estateResultItem }: Props) {
  const classes = useStyles();
  const context = useContext(EstateResultsPageContext);

  const [collectionInput, setCollectionInput] = useState<EstateResultItem_OneViewCollectionInput>({
    clientNameOrNumber: 'N/A',
    types: [''],
    dateReported: '',
    dateAssigned: '',
    status: 'N/A',
    originalAmount: 'N/A',
    balance: 'N/A',
    dateOfDelinquency: '',
    accountDesignatorCode: 'N/A',
    creditorClassification: 'N/A',
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [customFields, setCustomFields] = useState<CustomResultItemField[]>([]);
  const [selectedDateAssigned, setSelectedDateAssigned] = useState<Date>();
  const [selectedDateReported, setSelectedDateReported] = useState<Date>();
  const [selectedDateOfDelinquency, setSelectedDateOfDelinquency] = useState<Date>();
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedAccountDesignatorCode, setSelectedAccountDesignatorCode] = useState('');
  const [selectedCreditorClassification, setSelectedCreditorClassification] = useState('');

  // types are not displayed on the results page and are mostly redundant of the creditorClassificationCode
  const fields = ['Client Name or Number', 'Types', 'Original Amount', 'Balance'];
  const fieldKeys = ['clientNameOrNumber', 'types', 'originalAmount', 'balance'];

  const collectionStatusCodes = {
    D: `UNPAID - Subject has not satisfied debt.`,
    F: `FINANCIAL COUNSELOR - Identifies that a subject is receiving professional guidance on financial matters, and is under a payment plan.`,
    J: `ADJUSTMENT - Settlement of a debt in which full payment is not made or when the amount involved is not certain.`,
    M: `WAGE EARNER - Chapter 13 (Debtors with regular income). This chapter allows an individual to reorganize finances and protect assets while a court approved repayment plan is in effect.`,
    N: `NEW LISTING - Collection account which has just been turned over for collection of past due debt.`,
    P: `PAID - Subject has satisfied debt.`,
    S: `ACCOUNT DISPUTED - Merchant and consumer disagree on various particulars regarding merchandise, terms of agreement or amount owing.`,
    T: `PAYMENT - Subject submits portion of money owing.`,
    U: `STATUS UNKNOWN - Indicates status is not verified.`,
    X: `CHECKED - As of the date reported, the balance was not paid and the account was verified at the request of the consumer via a dispute.`,
    Z: `IN BANKRUPTCY - The legal process under the Federal Bankruptcy Act by which debtors are granted some form of relief from their financial obligations.`,
  };

  const accountDesignatorCodes = {
    A: 'Authorized User - This is an authorized user of this account, another individual has contractual responsibility.',
    B: 'On behalf of another person - The subject has financial responsibility for an account, which is used exclusively by another person.',
    C: 'Co-maker - The subject has co-signed for a loan, and will be responsible for payment if the borrower should default.',
    I: 'Individual Account - The subject of the report has contractual responsibility for this account and is primarily responsible for its payment.',
    J: 'Joint Account - The subject and another person (or persons) are jointly responsible for payment on this account.',
    M: 'Maker - The subject is responsible for payment of a loan, but a co-maker will be responsible for payment if maker defaults.',
    S: 'Shared, but otherwise undesignated - This code is an indication that the credit grantor knows that the subject and at least one other person share the account, but not enough information is available to designate the account as “J” or “A”.',
    T: "Terminated - The subject's relationship to this account has ended, although other parties who once shared the account may continue to maintain the account.",
    U: 'Undesignated',
    X: 'Deceased',
  };

  const creditorClassificationCode = {
    1: 'RETAIL',
    2: 'MEDICAL/HEALTH CARE',
    3: 'OIL COMPANY',
    4: 'GOVERNMENT',
    5: 'PERSONAL SERVICES',
    6: 'INSURANCE',
    7: 'EDUCATIONAL',
    8: 'BANKING',
    9: 'RENTAL/LEASING',
    10: 'UTILITIES',
    11: 'CABLE/CELLULAR',
    12: 'FINANCIAL',
    13: 'CREDIT UNION',
    14: 'AUTOMOTIVE',
    15: 'CHECK GUARANTEE',
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelectChange = (e: any) => {
    const name: string = e.target.name;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const value: any = e.target.value;
    collectionInput[name as keyof EstateResultItem_OneViewCollectionInput] = value;
    if (name === 'accountDesignatorCode') {
      setSelectedAccountDesignatorCode(value);
    } else if (name === 'status') {
      setSelectedStatus(value);
    } else {
      setSelectedCreditorClassification(value);
    }
    setEssentialData(collectionInput);
  };

  // the 'types' property forces explicit naming of the target property
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDateInputChange = (e: any, name: string) => {
    const date = new Date(e);
    const formattedDate = `${date.getMonth()}/${date.getDate()}/${date.getFullYear()}`;
    if (name === 'dateReported') {
      collectionInput.dateReported = formattedDate;
      setSelectedDateReported(e);
    } else if (name === 'dateAssigned') {
      collectionInput.dateAssigned = formattedDate;
      setSelectedDateAssigned(e);
    } else {
      collectionInput.dateOfDelinquency = formattedDate;
      setSelectedDateOfDelinquency(e);
    }
    setEssentialData(collectionInput);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleInputChange = (e: any, name: string) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const value: any = e.target.value;
    collectionInput[name as keyof EstateResultItem_OneViewCollectionInput] = value;
    const newInput = { ...collectionInput };
    setEssentialData(collectionInput);
    setCollectionInput(newInput);
  };

  const mapExistingEstateResultItemData = (estateResultItem: EstateResultItem) => {
    const essentialData = JSON.parse(estateResultItem.essentialData!);
    const newInput: EstateResultItem_OneViewCollectionInput = {
      clientNameOrNumber: essentialData.clientNameOrNumber || 'N/A',
      types: essentialData.types || [''],
      dateReported: essentialData.dateReported || '',
      dateAssigned: essentialData.dateAssigned || '',
      status: essentialData.status || 'N/A',
      originalAmount: essentialData.originalAmount || 'N/A',
      balance: essentialData.balance || 'N/A',
      dateOfDelinquency: essentialData.dateOfDelinquency || '',
      accountDesignatorCode: essentialData.accountDesignatorCode || 'N/A',
      creditorClassification: essentialData.creditorClassification || 'N/A',
      customFields: essentialData.customFields || {},
    };

    setCollectionInput(newInput);
    setSelectedDateAssigned(new Date(newInput.dateAssigned) || '');
    setSelectedDateReported(new Date(newInput.dateReported) || '');
    setSelectedDateOfDelinquency(new Date(newInput.dateOfDelinquency) || '');
    setSelectedStatus(newInput.status || '');
    setSelectedAccountDesignatorCode(newInput.accountDesignatorCode || '');
    setSelectedCreditorClassification(newInput.creditorClassification || '');
    setCustomFields(parseCustomFieldsJson(essentialData.customFields));
  };

  useEffect(() => {
    const essentialData: any = {};
    customFields.forEach((customField) => {
      essentialData[`customField${customField.id}`] = {
        id: customField.id,
        name: customField.field,
        value: customField.value,
      };
    });
    collectionInput.customFields = essentialData;
    setEssentialData(collectionInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFields, collectionInput]);

  useEffect(() => {
    if (estateResultItem) {
      mapExistingEstateResultItemData(estateResultItem);
    }
  }, [estateResultItem]);

  return (
    <>
      <Typography variant="h5" className={classes.title}>
        {context.isEditingEstateResultItem ? 'Edit Bill in Collections' : 'Add Bill in Collections'}
      </Typography>
      <Grid className={classes.container}>
        <Grid>
          {fields.map((field, index) => {
            return (
              <Grid className={classes.container}>
                <Grid>
                  <TextField
                    className={classes.inputField}
                    fullWidth
                    name={`${fieldKeys[index]}`}
                    label={`${field}`}
                    value={
                      context.isEditingEstateResultItem
                        ? collectionInput[
                            `${fieldKeys[index] as keyof EstateResultItem_OneViewCollectionInput}`
                          ]
                        : null
                    }
                    variant="outlined"
                    onChange={(e) => handleInputChange(e, fieldKeys[index])}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
              </Grid>
            );
          })}
          <Grid className={classes.container}>
            <Grid className={classes.selectContainer}>
              <Typography variant="caption">Account Designator Code</Typography>
              <Select
                required={true}
                fullWidth
                variant="outlined"
                name="accountDesignatorCode"
                value={selectedAccountDesignatorCode}
                onChange={handleSelectChange}
              >
                {Object.keys(accountDesignatorCodes).map((key: string) => {
                  return (
                    <MenuItem
                      key={key}
                      value={accountDesignatorCodes[key as keyof typeof accountDesignatorCodes]}
                    >
                      <Typography noWrap>
                        {accountDesignatorCodes[key as keyof typeof accountDesignatorCodes]}
                      </Typography>
                    </MenuItem>
                  );
                })}
              </Select>
            </Grid>
          </Grid>
          <Grid className={classes.container}>
            <Grid className={classes.selectContainer}>
              <Typography variant="caption">Collection Status</Typography>
              <Select
                fullWidth
                name="status"
                variant="outlined"
                value={selectedStatus}
                onChange={handleSelectChange}
              >
                {Object.keys(collectionStatusCodes).map((key: string) => {
                  return (
                    <MenuItem
                      key={key}
                      value={collectionStatusCodes[key as keyof typeof collectionStatusCodes]}
                    >
                      <Typography noWrap>
                        {collectionStatusCodes[key as keyof typeof collectionStatusCodes]}
                      </Typography>
                    </MenuItem>
                  );
                })}
              </Select>
            </Grid>
          </Grid>
          <Grid className={classes.container}>
            <Grid className={classes.selectContainer}>
              <Typography variant="caption">Creditor Classification</Typography>
              <Select
                fullWidth
                name="creditorClassification"
                variant="outlined"
                value={selectedCreditorClassification}
                onChange={handleSelectChange}
              >
                {Object.keys(creditorClassificationCode).map((key: string, index: number) => {
                  return (
                    <MenuItem
                      key={key}
                      value={
                        creditorClassificationCode[index as keyof typeof creditorClassificationCode]
                      }
                    >
                      <Typography noWrap>
                        {
                          creditorClassificationCode[
                            index as keyof typeof creditorClassificationCode
                          ]
                        }
                      </Typography>
                    </MenuItem>
                  );
                })}
              </Select>
            </Grid>
          </Grid>
        </Grid>
        <Grid className={classes.dateField}>
          <Grid className={classes.datePicker} item xs={12} sm={6}>
            <Typography variant="caption">Date Reported</Typography>
            <DatePicker
              orientation="landscape"
              value={selectedDateReported}
              minDate={selectedDateReported}
              onChange={(e) => handleDateInputChange(e, 'dateReported')}
              format={datePickerFormat}
              className={classes.inputField}
            />
          </Grid>
          <Grid className={classes.datePicker} item xs={12} sm={6}>
            <Typography variant="caption">Date Assigned</Typography>
            <DatePicker
              orientation="landscape"
              value={selectedDateAssigned}
              maxDate={selectedDateReported}
              onChange={(e) => handleDateInputChange(e, 'dateAssigned')}
              format={datePickerFormat}
              className={classes.inputField}
            />
          </Grid>

          <Grid className={classes.datePicker} item xs={12} sm={6}>
            <Typography variant="caption">Date of Delinquency</Typography>
            <DatePicker
              orientation="landscape"
              value={selectedDateOfDelinquency}
              maxDate={selectedDateReported}
              onChange={(e) => handleDateInputChange(e, 'dateOfDelinquency')}
              format={datePickerFormat}
              className={classes.inputField}
            />
          </Grid>
        </Grid>
        <Typography variant="h5" className={classes.title}>
          Custom Fields
        </Typography>
        <Grid className={classes.container}>
          <Grid item xs={12} sm={12}>
            {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              customFields.map((customField: CustomResultItemField, index: number) => {
                return (
                  <NewEssentialData
                    key={index}
                    customField={customField}
                    updateCustomField={(updatedCustomField: CustomResultItemField) => {
                      updateCustomField(updatedCustomField, customFields, setCustomFields);
                    }}
                    handleRemoveClick={() => {
                      removeCustomField(customField, customFields, setCustomFields);
                    }}
                  />
                );
              })
            }
          </Grid>
        </Grid>
        <div className={classes.container}>
          {!context.isEditingEstateResultItem && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              className={classes.button}
              onClick={() => {
                handleNewCustomFieldButtonClick(customFields, setCustomFields);
              }}
            >
              + Add new field
            </Button>
          )}
        </div>
      </Grid>
    </>
  );
}
